import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import * as Yup from "yup";
import type { FormikErrors, FormikTouched } from "formik";

import {
  Button as MuiButton,
  CardContent as MuiCardContent,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TextField as MuiTextField,
  IconButton,
  MenuItem,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Box, spacing } from "@mui/system";
import {
  createVoucher,
  deleteVoucher,
  fetchVouchers,
  updateVoucher,
} from "../../../redux/slices/accounting";
import useAppSelector from "../../../hooks/useAppSelector";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAuth from "../../../hooks/useAuth";
import { Add, Delete, Edit, Remove, Search } from "@mui/icons-material";
import { format } from "date-fns";
import { Field, FieldArray, Form, Formik } from "formik";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import useSnackbar from "../SnackbarHook";
import CustomSnackbar from "../SnackBar";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { fi } from "date-fns/locale";
import { toDateOnlyString } from "../../../utils/dateUtils";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const CardContent = styled(MuiCardContent)(spacing);

const Button = styled(MuiButton)(spacing);

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

type Mode = { mode: "create" | "read" | "edit"; voucherId?: number } | null;

type TransactionFormRow = {
  description: string;
  debit: string;
  credit: string;
  accountCode: number | "";
};

type VoucherFormValues = {
  description: string;
  date: Date | null;
  transactions: TransactionFormRow[];
};

const accounts: any[] = [
  {
    code: 100,
    name: "Aineelliset",
    rowId: "1.1",
  },
  {
    code: 101,
    name: "Aineettomat",
    rowId: "1.1",
  },
  {
    code: 102,
    name: "Sijoitukset",
    rowId: "1.1",
  },
  {
    code: 150,
    name: "Vaihto-omaisuus",
    rowId: "1.2",
  },
  {
    code: 151,
    name: "Saamiset",
    rowId: "1.2",
  },
  {
    code: 160,
    name: "MP-lainasaaminen osakkailta",
    rowId: "1.2",
  },
  {
    code: 161,
    name: "Muut saamiset",
    rowId: "1.2",
  },
  {
    code: 170,
    name: "Siirtosaamiset",
    rowId: "1.2",
  },
  {
    code: 190,
    name: "Pankkitili",
    rowId: "1.2",
  },
  {
    code: 200,
    name: "Edell. tilik. voitto (tappio)",
    rowId: "2.1",
  },
  {
    code: 230,
    name: "Tilikauden voitto (tappio)",
    rowId: "2.1",
  },
  {
    code: 250,
    name: "MP-laina",
    rowId: "2.2.1",
  },
  {
    code: 260,
    name: "Siirtovelat",
    rowId: "2.2.2",
  },
  {
    code: 300,
    name: "Yksikkömaksut",
    rowId: "3.1.1.1",
  },
  {
    code: 310,
    name: "Perusmaksut",
    rowId: "3.1.1.1",
  },
  {
    code: 320,
    name: "Liittymismaksut",
    rowId: "3.1.1.1",
  },
  {
    code: 330,
    name: "Kuljetusmaksut",
    rowId: "3.1.1.1",
  },
  {
    code: 340,
    name: "Muut tuotot",
    rowId: "3.1.1.1",
  },
  {
    code: 350,
    name: "Viivästysmaksut",
    rowId: "3.1.1.1",
  },
  {
    code: 400,
    name: "Lanaus",
    rowId: "3.1.1.2.1.1",
  },
  {
    code: 410,
    name: "Sora, murske",
    rowId: "3.1.1.2.1.1",
  },
  {
    code: 420,
    name: "Pölynsidonta",
    rowId: "3.1.1.2.1.1",
  },
  {
    code: 430,
    name: "Vesakontorjunta, niitto",
    rowId: "3.1.1.2.1.1",
  },
  {
    code: 440,
    name: "Muut kesätyöt",
    rowId: "3.1.1.2.1.1",
  },
  {
    code: 450,
    name: "Tarvikkeet kesällä",
    rowId: "3.1.1.2.1.1",
  },
  {
    code: 460,
    name: "Ojat",
    rowId: "3.1.1.2.1.2",
  },
  {
    code: 470,
    name: "Rummut",
    rowId: "3.1.1.2.1.2",
  },
  {
    code: 480,
    name: "Muut korjaukset",
    rowId: "3.1.1.2.1.2",
  },
  {
    code: 500,
    name: "Auraus",
    rowId: "3.1.1.2.1.3",
  },
  {
    code: 510,
    name: "Hiekoitus",
    rowId: "3.1.1.2.1.3",
  },
  {
    code: 520,
    name: "Aurausviitat",
    rowId: "3.1.1.2.1.3",
  },
  {
    code: 530,
    name: "Muut talvityöt",
    rowId: "3.1.1.2.1.3",
  },
  {
    code: 540,
    name: "Tarvikkeet talvella",
    rowId: "3.1.1.2.1.3",
  },
  {
    code: 600,
    name: "Kokouskulut",
    rowId: "3.1.1.2.2",
  },
  {
    code: 610,
    name: "Palkat ja palkkiot",
    rowId: "3.1.1.2.2",
  },
  {
    code: 620,
    name: "Kirjanpito",
    rowId: "3.1.1.2.2",
  },
  {
    code: 630,
    name: "Matkakulut, päivärahat",
    rowId: "3.1.1.2.2",
  },
  {
    code: 640,
    name: "Postikulut",
    rowId: "3.1.1.2.2",
  },
  {
    code: 650,
    name: "Pankkikulut",
    rowId: "3.1.1.2.2",
  },
  {
    code: 660,
    name: "Muut hallintokulut",
    rowId: "3.1.1.2.2",
  },
  {
    code: 670,
    name: "Jäsenmaksut",
    rowId: "3.1.1.2.2",
  },
  {
    code: 680,
    name: "Ulosottokulut",
    rowId: "3.1.1.2.2",
  },
  {
    code: 690,
    name: "Vakuutukset",
    rowId: "3.1.1.2.2",
  },
  {
    code: 700,
    name: "Korkotulot",
    rowId: "3.1.2",
  },
  {
    code: 800,
    name: "Satunnaiset tuotot",
    rowId: "3.2",
  },
  {
    code: 850,
    name: "Satunnaiset menot",
    rowId: "3.2",
  },
  {
    code: 900,
    name: "Avustukset kunnalta",
    rowId: "3.3",
  },
  {
    code: 910,
    name: "Avustukset valtiolta",
    rowId: "3.3",
  },
  {
    code: 999,
    name: "Tilikauden voitto (tappio)",
    rowId: "3",
  },
];

const parseAmt = (v: unknown) => {
  if (v === "" || v == null) return 0;
  const s = String(v).replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

const accountCodes = accounts.map((a) => a.code);

const txRowSchema = Yup.object({
  description: Yup.string().max(120, "Max 120 merkkiä").required("Anna kuvaus"),
  accountCode: Yup.mixed()
    .transform((v) => (v === "" || v == null ? null : Number(v)))
    .oneOf(accountCodes, "Valitse tili")
    .required("Valitse tili"),
  debit: Yup.string()
    .transform((v) => (v == null ? "" : String(v)))
    .test("is-number", "Debet pitää olla numero", (v) =>
      v === "" ? true : Number.isFinite(parseAmt(v))
    )
    .test("non-negative", "Debet ei voi olla negatiivinen", (v) =>
      v === "" ? true : parseAmt(v) >= 0
    )
    .required("Anna Debet"),
  credit: Yup.string()
    .transform((v) => (v == null ? "" : String(v)))
    .test("is-number", "Credit pitää olla numero", (v) =>
      v === "" ? true : Number.isFinite(parseAmt(v))
    )
    .test("non-negative", "Credit ei voi olla negatiivinen", (v) =>
      v === "" ? true : parseAmt(v) >= 0
    )
    .required("Anna credit"),
});

const validationSchema = Yup.object({
  description: Yup.string()
    .required("Nimi on pakollinen")
    .max(160, "Max 160 merkkiä"),
  date: Yup.date()
    .typeError("Valitse kelvollinen pvm")
    .required("Pvm on pakollinen"),
  transactions: Yup.array()
    .of(txRowSchema)
    .min(1, "Lisää vähintään yksi tapahtuma"),
});

function VoucherForm(props: any) {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const voucher = useAppSelector((state) =>
    state.accounting.vouchers.find((voucher) => voucher.id === props.voucherId)
  );

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const { mode } = props;

  const handleSubmit = async (
    // eslint-disable-next-line
    values: any,
    // eslint-disable-next-line
    { resetForm, setErrors, setStatus, setSubmitting }: any
  ) => {
    try {
      const payload = {
        ...values,
        date: values.date ? toDateOnlyString(values.date) : null,
      };
      if (voucher && voucher.id !== undefined) {
        dispatch(updateVoucher(voucher.id, payload));
      } else {
        dispatch(createVoucher(payload, associationId));
      }

      resetForm();
      setStatus({ sent: true });
      setSubmitting(false);

      props.onComplete(true);
      props.handleMode(null);
    } catch (error: unknown) {
      setStatus({ sent: false });
      setErrors({ submit: "Error" });
      setSubmitting(false);
      props.onComplete(false);
    }
  };

  return (
    <Formik<VoucherFormValues>
      initialValues={
        voucher
          ? {
              description: voucher.description,
              date: voucher.date ? new Date(voucher.date) : null,
              transactions: voucher.transactions ?? [
                { description: "", debit: "", credit: "", accountCode: "" },
              ],
            }
          : {
              description: "",
              date: null,
              transactions: [
                { description: "", debit: "", credit: "", accountCode: "" },
              ],
            }
      }
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, setFieldValue, errors, touched }) => {
        return (
          <Card mb={6}>
            <CardContent>
              <Form>
                <Typography variant="h5" gutterBottom>
                  Tosite
                </Typography>

                {/* Voucher Details */}
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={fi}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Field
                        name="description"
                        as={TextField}
                        label="Nimi"
                        fullWidth
                        error={
                          touched.description && Boolean(errors.description)
                        }
                        helperText={touched.description && errors.description}
                        disabled={mode === "read"}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <DatePicker
                        label="Pvm"
                        value={values.date}
                        inputFormat="dd.MM.yyyy"
                        onChange={(date: Date | null) =>
                          setFieldValue("date", date)
                        }
                        disabled={mode === "read"}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            error={touched.date && Boolean(errors.date)}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </LocalizationProvider>

                <Typography
                  variant="h6"
                  gutterBottom
                  style={{ marginTop: "20px" }}
                >
                  Tapahtumat
                </Typography>

                <FieldArray name="transactions">
                  {({ push, remove }) => (
                    <div>
                      {values.transactions.map(
                        (transaction: any, index: any) => (
                          <Grid
                            container
                            spacing={2}
                            key={index}
                            alignItems="center"
                          >
                            <Grid item xs={3}>
                              <Field
                                name={`transactions[${index}].description`}
                                as={TextField}
                                label="Kuvaus"
                                fullWidth
                                error={
                                  !!touched.transactions?.[index]
                                    ?.description &&
                                  !!(errors.transactions as any)?.[index]
                                    ?.description
                                }
                                helperText={
                                  touched.transactions?.[index]?.description &&
                                  (errors.transactions as any)?.[index]
                                    ?.description
                                }
                                disabled={mode === "read"}
                              />
                            </Grid>

                            <Grid item xs={2}>
                              <Field
                                name={`transactions[${index}].debit`}
                                as={TextField}
                                label="Debet"
                                fullWidth
                                error={
                                  !!touched.transactions?.[index]?.debit &&
                                  !!(errors.transactions as any)?.[index]?.debit
                                }
                                helperText={
                                  touched.transactions?.[index]?.debit &&
                                  (errors.transactions as any)?.[index]?.debit
                                }
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  // Optionally convert comma to dot automatically
                                  const value = e.target.value.replace(
                                    ",",
                                    "."
                                  );
                                  setFieldValue(
                                    `transactions[${index}].debit`,
                                    value
                                  );
                                }}
                                disabled={mode === "read"}
                              />
                            </Grid>

                            <Grid item xs={2}>
                              <Field
                                name={`transactions[${index}].credit`}
                                as={TextField}
                                label="Credit"
                                fullWidth
                                error={
                                  !!touched.transactions?.[index]?.credit &&
                                  !!(errors.transactions as any)?.[index]
                                    ?.credit
                                }
                                helperText={
                                  touched.transactions?.[index]?.credit &&
                                  (errors.transactions as any)?.[index]?.credit
                                }
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  // Optionally convert comma to dot automatically
                                  const value = e.target.value.replace(
                                    ",",
                                    "."
                                  );
                                  setFieldValue(
                                    `transactions[${index}].credit`,
                                    value
                                  );
                                }}
                                disabled={mode === "read"}
                              />
                            </Grid>

                            <Grid item xs={3}>
                              <Field
                                name={`transactions[${index}].accountCode`}
                                as={TextField}
                                label="Tili"
                                fullWidth
                                select
                                error={
                                  !!touched.transactions?.[index]
                                    ?.accountCode &&
                                  !!(errors.transactions as any)?.[index]
                                    ?.accountCode
                                }
                                helperText={
                                  touched.transactions?.[index]?.accountCode &&
                                  (errors.transactions as any)?.[index]
                                    ?.accountCode
                                }
                                disabled={mode === "read"}
                              >
                                {accounts.map((account) => (
                                  <MenuItem
                                    key={account.code}
                                    value={account.code}
                                  >
                                    {account.code + " - " + account.name}
                                  </MenuItem>
                                ))}
                              </Field>
                            </Grid>

                            <Grid item xs={2}>
                              <IconButton
                                color="secondary"
                                onClick={() =>
                                  values.transactions.length > 1 &&
                                  remove(index)
                                }
                                disabled={mode === "read"}
                              >
                                <Remove />
                              </IconButton>
                            </Grid>
                          </Grid>
                        )
                      )}

                      {/* Add Transaction Button */}
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() =>
                          push({
                            description: "",
                            debit: "",
                            credit: "",
                            accountCode: "",
                          })
                        }
                        startIcon={<Add />}
                        style={{ marginTop: "10px" }}
                        disabled={mode === "read"}
                      >
                        Lisää tapahtuma
                      </Button>
                    </div>
                  )}
                </FieldArray>

                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  style={{ marginTop: "32px", marginRight: "8px" }}
                  disabled={mode === "read"}
                  mt={3}
                >
                  Lähetä
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  style={{ marginTop: "32px" }}
                  mt={3}
                  onClick={() => props.handleMode(null)}
                >
                  Peruuta
                </Button>
              </Form>
            </CardContent>
          </Card>
        );
      }}
    </Formik>
  );
}

type VoucherListProps = {
  rows: any[];
  handleMode: (
    mode: "create" | "read" | "edit" | null,
    voucherId?: number
  ) => void;
  handleDelete: (row: any) => void;
  from: Date | null;
  to: Date | null;
  setFrom: (d: Date | null) => void;
  setTo: (d: Date | null) => void;
  runSearch: () => void;
  mode: Mode;
};

function VoucherList(props: VoucherListProps) {
  const { status } = useAppSelector((state) => state.accounting);
  const { from, to, setFrom, setTo, runSearch } = props;

  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Tositteet
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
          <Box
            display="flex"
            gap={2}
            alignItems="center"
            mb={2}
            flexWrap="wrap"
          >
            <DatePicker
              label="Alkaen"
              inputFormat="dd.MM.yyyy"
              value={from}
              onChange={(v: Date | null) => setFrom(v)}
              maxDate={to ?? undefined}
              renderInput={(params) => <TextField {...params} size="small" />}
              disabled
            />
            <DatePicker
              label="Päättyy"
              inputFormat="dd.MM.yyyy"
              value={to}
              onChange={(v: Date | null) => setTo(v)}
              minDate={from ?? undefined}
              renderInput={(params) => <TextField {...params} size="small" />}
              disabled
            />
            {/* <Button variant="contained" onClick={runSearch}>
              Hae
            </Button> */}
          </Box>
        </LocalizationProvider>
        <Box mb={2}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<Add />}
            onClick={() => props.handleMode("create")}
          >
            Lisää tosite
          </Button>
        </Box>
      </CardContent>

      <Paper>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell>Kuvaus</TableCell>
              <TableCell>Pvm</TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {status === "loading" ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : props.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Ei tositteita
                </TableCell>
              </TableRow>
            ) : (
              props.rows.map((row: any) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.description}</TableCell>
                  <TableCell>
                    {row.date
                      ? format(new Date(row.date), "dd.MM.yyyy", { locale: fi })
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Näytä tiedot">
                      <IconButton
                        size="small"
                        onClick={() => props.handleMode("read", row.id)}
                      >
                        <Search fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Muokkaa">
                      <IconButton
                        size="small"
                        onClick={() => props.handleMode("edit", row.id)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Poista">
                      <IconButton
                        size="small"
                        onClick={() => props.handleDelete(row)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function Voucher() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { activeFiscalYear, vouchers } = useAppSelector(
    (state) => state.accounting
  );

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const [mode, setMode] = useState<Mode>(null);
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);

  const handleMode = (
    m: "create" | "read" | "edit" | null,
    voucherId?: number
  ) => {
    if (m === null) setMode(null);
    else setMode({ mode: m, voucherId });
  };

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const handleDelete = async (voucher: any) => {
    const confirmed = window.confirm(
      `Haluatko varmasti poistaa tositteen "${voucher.description}"?`
    );
    if (confirmed) {
      setProgress(100);
      try {
        await dispatch(deleteVoucher(voucher.id));
        setSnackbarMessage("Tosite poistettu onnistuneesti!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } catch (e) {
        setSnackbarMessage("Virhe poistettaessa tositetta.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    }
  };

  const handleComplete = (success: any) => {
    setProgress(100);
    if (success) {
      setSnackbarMessage("Tosite tallennettu onnistuneesti!");
      setSnackbarSeverity("success");
    } else {
      setSnackbarMessage("Virhe tallennettaessa tositetta.");
      setSnackbarSeverity("error");
    }
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  const runSearch = () => {
    if (!from || !to) return;
    dispatch(
      fetchVouchers({
        associationId,
        from: format(from, "yyyy-MM-dd"),
        to: format(to, "yyyy-MM-dd"),
      })
    );
  };

  useEffect(() => {
    if (activeFiscalYear) {
      setFrom(new Date(activeFiscalYear.from));
      setTo(new Date(activeFiscalYear.to));
    }
  }, [activeFiscalYear]);

  useEffect(() => {
    if (!currentUser || !activeFiscalYear) return;
    dispatch(
      fetchVouchers({
        associationId,
        from: format(new Date(activeFiscalYear.from), "yyyy-MM-dd"),
        to: format(new Date(activeFiscalYear.to), "yyyy-MM-dd"),
      })
    );
  }, [dispatch, currentUser, activeFiscalYear, associationId]);

  return (
    <React.Fragment>
      <Helmet title="Tositteet" />
      <Typography variant="h3" gutterBottom display="inline">
        Tositteet
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/">
          Talous
        </Link>
        <Typography>Tositteet</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      {mode !== null ? (
        <VoucherForm
          handleMode={handleMode}
          mode={mode.mode}
          voucherId={mode.voucherId}
          onComplete={handleComplete}
        />
      ) : (
        <VoucherList
          rows={vouchers}
          handleMode={handleMode}
          mode={mode}
          handleDelete={handleDelete}
          from={from}
          to={to}
          setFrom={setFrom}
          setTo={setTo}
          runSearch={runSearch}
        />
        // <VoucherList
        //   rows={vouchers}
        //   handleMode={handleMode}
        //   mode={mode}
        //   //handleEditVoucher={handleEditVoucher}
        //   handleDelete={handleDelete}
        //   //openForm={open}
        // />
      )}

      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={handleCloseSnackbar}
      />
    </React.Fragment>
  );
}

export default Voucher;
